// VFR-sectional-style airport symbology rendered as small map images.
//
// We synthesize SVG icons and load them into the MapLibre style as named
// images. The airports layer then references these via a 'match'
// expression keyed on the feature’s `hasFuel` flag (set server-side by
// /api/airports based on runway data + frequency listings + a manual
// override list, until FAA NASR airport import lands).
//
// Symbology (FAA sectional convention):
//
//   apt-fuel      — magenta ring + 4-tick crosshair (fuel available)
//   apt-no-fuel   — magenta open ring (no fuel available)
//   apt-towered   — blue ring + crosshair (towered field, fuel implied)
//   apt-seaplane  — cyan ring + anchor tick (seaplane base)
//
// We render each symbol on a 32x32 canvas at 2x DPR so it stays crisp
// on iPad / Retina screens. The actual on-map size is controlled by
// `icon-size` in the layer style.
//
// Colors are tuned for legibility on BOTH the Sectional (cream
// background) and the Satellite imagery (dark/varied). The black halo
// stroke is what makes them readable on either.

import type { Map as MlMap, ExpressionSpecification } from 'maplibre-gl';
import { ensureSvgImages } from './iconRegistry';

export type AirportSymbol =
  | 'apt-towered'     // towered (Class B/C/D): blue + crosshair
  | 'apt-fuel'        // fuel available: magenta + crosshair
  | 'apt-no-fuel'     // no fuel: magenta open ring
  | 'apt-seaplane'    // seaplane base: cyan w/ anchor tick
  | 'heli'            // heliport: magenta H inside circle (FAA sectional convention)
  | 'wxcam';          // weather camera: little camera icon, FAA WeatherCams sites

interface SymbolDef {
  id: AirportSymbol;
  /** SVG markup. Must be a complete <svg> document with width/height. */
  svg: string;
  /** Optional per-symbol pixel size override (default 40 for airport
   *  rings; 32 for heli + wxcam). */
  size?: number;
}

// Color palette
const TOWERED_BLUE  = '#4f8bff';
const NONTOWER_MAG  = '#e76bff';
const SEAPLANE_CYAN = '#22d3ee';
const HALO_DARK     = '#0b0d0f';

/** Build a centered VFR-style airport icon at 40px square (bumped up
 *  from 32 so the on-map presence reads better at the typical en-route
 *  zoom levels the boss flies at). */
function svgRing(color: string, withCrosshair: boolean, withAnchorTick = false): string {
  const cx = 20, cy = 20, r = 8;
  const ring = `
    <circle cx="${cx}" cy="${cy}" r="${r}"
            fill="none" stroke="${HALO_DARK}" stroke-width="4.2" />
    <circle cx="${cx}" cy="${cy}" r="${r}"
            fill="none" stroke="${color}" stroke-width="2.6" />
  `;
  const tickStroke = `stroke="${color}" stroke-width="3.0" stroke-linecap="round"`;
  const tickHalo   = `stroke="${HALO_DARK}" stroke-width="4.8" stroke-linecap="round"`;
  let crosshair = '';
  if (withCrosshair) {
    // Four ticks at NSEW outside the ring — the FAA sectional symbol
    // for an attended airport with fuel.
    const lines = [
      `x1="${cx}" y1="4"  x2="${cx}" y2="${cy - r - 1}"`,            // N
      `x1="${cx}" y1="${cy + r + 1}" x2="${cx}" y2="36"`,             // S
      `x1="4"    y1="${cy}" x2="${cx - r - 1}" y2="${cy}"`,           // W
      `x1="${cx + r + 1}" y1="${cy}" x2="36" y2="${cy}"`,             // E
    ];
    crosshair =
      lines.map(p => `<line ${p} ${tickHalo} />`).join('') +
      lines.map(p => `<line ${p} ${tickStroke} />`).join('');
  }
  let anchor = '';
  if (withAnchorTick) {
    anchor = `
      <line x1="${cx}" y1="${cy + r + 1}" x2="${cx}" y2="34" ${tickHalo} />
      <line x1="${cx}" y1="${cy + r + 1}" x2="${cx}" y2="34" ${tickStroke} />
      <line x1="${cx - 5}" y1="33" x2="${cx + 5}" y2="33" ${tickHalo} />
      <line x1="${cx - 5}" y1="33" x2="${cx + 5}" y2="33" ${tickStroke} />
    `;
  }
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
      ${crosshair}
      ${ring}
      ${anchor}
    </svg>
  `.trim();
}

/**
 * Heliport icon — the FAA sectional convention is a small circle with a
 * letter H inscribed inside (or, for IFR plates, a square). We use the
 * VFR sectional circle-and-H form so the icon carries through whichever
 * basemap is active. The H is drawn slightly bolder than the airport
 * crosshair ticks so it reads at the smaller heliport icon-size.
 *
 * Wiley Anderson feedback 2026-05-09 — fire / HEMS / SAR pilots want
 * heliports as an opt-in layer on the map.
 */
function svgHeliport(color: string): string {
  const cx = 16, cy = 16, r = 7;
  // Letter H: two vertical bars + horizontal crossbar inside the ring.
  // Geometry is hand-tuned at 32x32 viewBox so the H sits visually
  // centered without optical drift.
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="${cx}" cy="${cy}" r="${r}"
              fill="none" stroke="${HALO_DARK}" stroke-width="4.0" />
      <circle cx="${cx}" cy="${cy}" r="${r}"
              fill="none" stroke="${color}" stroke-width="2.4" />
      <!-- H letter, drawn as 3 strokes; halo first then color -->
      <line x1="12" y1="11" x2="12" y2="21" stroke="${HALO_DARK}" stroke-width="3.6" stroke-linecap="round" />
      <line x1="20" y1="11" x2="20" y2="21" stroke="${HALO_DARK}" stroke-width="3.6" stroke-linecap="round" />
      <line x1="12" y1="16" x2="20" y2="16" stroke="${HALO_DARK}" stroke-width="3.6" stroke-linecap="round" />
      <line x1="12" y1="11" x2="12" y2="21" stroke="${color}"     stroke-width="2.0" stroke-linecap="round" />
      <line x1="20" y1="11" x2="20" y2="21" stroke="${color}"     stroke-width="2.0" stroke-linecap="round" />
      <line x1="12" y1="16" x2="20" y2="16" stroke="${color}"     stroke-width="2.0" stroke-linecap="round" />
    </svg>
  `.trim();
}

/**
 * Weather camera icon — small camera silhouette with a colored body.
 * Distinct shape (not a ring like airports/heliports) so the eye reads
 * it as 'this is a different kind of thing on the map'. Used for
 * the FAA WeatherCams layer (Wiley Anderson feedback 2026-05-09).
 */
function svgWxCam(color: string): string {
  // 32x32 viewBox. Camera body 22x14 with a hump for the lens housing.
  // Halo first, then color stroke, then a small lens 'eye' dot.
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <!-- halo for legibility on chart + imagery -->
      <rect x="4" y="10" width="24" height="16" rx="3" fill="${HALO_DARK}" stroke="${HALO_DARK}" stroke-width="2.4" />
      <rect x="11" y="6" width="10" height="6" rx="1.5" fill="${HALO_DARK}" />
      <!-- body -->
      <rect x="5.4" y="11.4" width="21.2" height="13.2" rx="2.2" fill="${color}" />
      <rect x="12" y="7" width="8" height="5" rx="1" fill="${color}" />
      <!-- lens -->
      <circle cx="16" cy="18" r="4" fill="${HALO_DARK}" />
      <circle cx="16" cy="18" r="2.2" fill="#ffffff" />
      <!-- record dot top right -->
      <circle cx="23" cy="15" r="1.2" fill="#ef4444" />
    </svg>
  `.trim();
}

// Per-symbol size: airport rings are 40px, the heli + wxcam icons
// were originally drawn against a 32px viewBox so we keep that size
// when pre-rendering them. The shared registry honors per-symbol
// `size` overrides (default falls through to opts.defaultSize).
const SYMBOLS: SymbolDef[] = [
  { id: 'apt-towered',  svg: svgRing(TOWERED_BLUE,  /*crosshair*/ true) },
  { id: 'apt-fuel',     svg: svgRing(NONTOWER_MAG,  /*crosshair*/ true) },
  { id: 'apt-no-fuel',  svg: svgRing(NONTOWER_MAG,  /*crosshair*/ false) },
  { id: 'apt-seaplane', svg: svgRing(SEAPLANE_CYAN, /*crosshair*/ false, /*anchor*/ true) },
  { id: 'heli',         svg: svgHeliport(NONTOWER_MAG), size: 32 },
  // Weather camera — muted yellow-ish so it doesn't compete with
  // ember-orange (plan line) or magenta (airports). FAA WeatherCams
  // brand is light teal, but on a sectional that's hard to read; this
  // mustard reads on every basemap.
  { id: 'wxcam',        svg: svgWxCam('#fbbf24'), size: 32 },
];

/**
 * Load each airport symbol into the live map style. Idempotent —
 * calling twice is safe. Must be called after map 'load' (or
 * 'styledata') so MapLibre's image atlas is ready.
 *
 * Returns a Promise that resolves once all icons are registered.
 * Caller can fire-and-forget; addOverlays() tolerates icons resolving
 * after the layer is added (MapLibre re-renders on each addImage).
 */
export function ensureAirportIcons(map: MlMap): Promise<void> {
  return ensureSvgImages(map, SYMBOLS, {
    // Default 40px (airport rings); heli + wxcam carry their own 32px
    // override on their SymbolDef entries so the shared registry
    // doesn't need a special-case.
    defaultSize: 40,
    logTag: '[airportIcons]',
  });
}

/**
 * MapLibre style expression that maps an airport feature's properties
 * to one of the registered symbol ids. Order matters — first match wins.
 *
 * Source-of-truth fields come straight from FAA NASR APT.txt via the
 * /api/admin/import-nasr cron (parsed in api/admin/import-nasr.ts):
 *
 *   hasTower  ATCT located on airport (col 981 of APT.txt, Y/N)
 *   hasFuel   Any fuel type published in the 8×5-char fuel slot
 *             (col 901, e.g. 100LL / A / A++ / A1 / J / MOGAS)
 *
 * Symbology rules (matches FAA Sectional convention):
 *   1. seaplane_base       → apt-seaplane (cyan ring + anchor)
 *   2. hasTower === true   → apt-towered  (blue ring + crosshair)
 *   3. hasFuel === true    → apt-fuel     (magenta + crosshair)
 *   4. fallback            → apt-no-fuel  (magenta open ring)
 *
 * Pre-NASR fallback path: when the bbox endpoint is still serving
 * OurAirports rows (no hasTower flag set), the `scheduled` heuristic
 * stands in. That path goes away once every prod manifest has been
 * re-imported with airports.json present.
 */
export const AIRPORT_ICON_EXPRESSION: ExpressionSpecification = [
  'case',
  ['==', ['get', 'type'], 'seaplane_base'], 'apt-seaplane',
  ['==', ['get', 'hasTower'], true], 'apt-towered',
  // Legacy fallback for OurAirports-sourced rows that don't carry
  // hasTower yet. Drops cleanly once NASR airports are everywhere.
  ['any',
    ['==', ['get', 'type'], 'large_airport'],
    ['==', ['get', 'scheduled'], true],
  ], 'apt-towered',
  ['==', ['get', 'hasFuel'], true], 'apt-fuel',
  /* default */ 'apt-no-fuel',
] as unknown as ExpressionSpecification;
